import { CSSProperties } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { Pill } from '@shared/components/pill';
import { isDeletedEntity } from '@shared/data/entity/Entity';
import { RelatedEntityField, RelatedItem } from '@shared/data/entity/fields/RelatedEntityField';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { CardCollectionViewFieldContainer } from './CardCollectionViewFieldContainer';

interface Props {
  field: RelatedEntityField;
  component: API.AnyComponent;
  style: StyleProp<ViewStyle>;
  pillStyle?: StyleProp<ViewStyle>;
  pillTextStyle?: CSSProperties;
}

const MAX_CARDS_TO_SHOW = 3;

const relatedCardStyles: StyleProp<ViewStyle> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginLeft: -grid(0.5),
};

export function RelatedCardCollectionViewField({
  field,
  component,
  style,
  pillStyle,
  pillTextStyle,
}: Props) {
  if (field.isEmpty) {
    return null;
  }

  const validItems = field.items.filter(item =>
    field.relatedEntities.some(relatedItem => relatedItem.id === item.id),
  );
  const itemsToShow = validItems.slice(0, MAX_CARDS_TO_SHOW);
  const remainingCardCount = validItems.length - MAX_CARDS_TO_SHOW;
  const defaultEntityName = i18n('workflowTemplate.defaultRelatedCard', {
    workflowName: getComponentLabel(component),
  });

  return (
    <CardCollectionViewFieldContainer style={[relatedCardStyles, style]}>
      {itemsToShow.map(item => (
        <RelatedCardPill
          key={item.id}
          item={item}
          field={field}
          defaultEntityName={defaultEntityName}
          pillStyle={pillStyle}
          pillTextStyle={pillTextStyle}
        />
      ))}
      {remainingCardCount > 0 && <Pill sections={[`+${remainingCardCount}`]} noBorder />}
    </CardCollectionViewFieldContainer>
  );
}

interface RelatedCardPillProps {
  item: RelatedItem;
  field: RelatedEntityField;
  defaultEntityName: string;
  pillStyle?: StyleProp<ViewStyle>;
  pillTextStyle?: CSSProperties;
}

function RelatedCardPill({
  item,
  field,
  pillStyle,
  pillTextStyle,
  defaultEntityName,
}: RelatedCardPillProps) {
  const entity = field.relatedEntities.find(e => e.id === item.id);
  const isDeleted = isDeletedEntity(entity);
  const quantity = item.quantity?.toString();
  const entityName = entity?.fields?.name ?? defaultEntityName;
  return (
    <Pill
      sections={[quantity, entityName].filter(notNil)}
      maxWidth={grid(32)}
      numberOfLines={2}
      italic={isDeleted}
      style={pillStyle}
      textStyle={pillTextStyle}
    />
  );
}
